import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, openAuthenticatedDownload } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, Warning, ClipboardText, ChatCircle, ArrowsClockwise, XCircle, Question, FileXls } from '@phosphor-icons/react';
import SearchBar from '../components/SearchBar';

const ACTIONS = [
  { value: 'unreviewed', label: 'Unreviewed', color: 'gray', icon: <ArrowsClockwise size={11} /> },
  { value: 'reviewed_ok', label: 'Reviewed OK', color: 'emerald', icon: <CheckCircle size={11} /> },
  { value: 'refund_pending', label: 'Refund Pending', color: 'red', icon: <Warning size={11} /> },
  { value: 'refund_done', label: 'Refund Done', color: 'blue', icon: <CheckCircle size={11} /> },
  { value: 'false_positive', label: 'False Positive', color: 'gray', icon: <XCircle size={11} /> },
  { value: 'needs_distributor_confirm', label: 'Need Distrib. Confirm', color: 'amber', icon: <Question size={11} /> },
];

const ACTION_LABELS = ACTIONS.reduce((m, a) => { m[a.value] = a; return m; }, {});

const colorChip = (color) => ({
  gray: 'bg-gray-100 text-gray-700',
  emerald: 'bg-emerald-100 text-emerald-700',
  red: 'bg-red-100 text-red-700',
  blue: 'bg-blue-100 text-blue-700',
  amber: 'bg-amber-100 text-amber-700',
}[color] || 'bg-gray-100 text-gray-700');

const categoryChip = (cat) => ({
  Refunded: 'bg-red-100 text-red-700',
  Pending: 'bg-yellow-100 text-yellow-700',
  Success: 'bg-emerald-100 text-emerald-700',
  Other: 'bg-gray-100 text-gray-700',
}[cat] || 'bg-gray-100 text-gray-700');

export default function HpclMatcherReviewPage() {
  const { reportId } = useParams();
  const [data, setData] = useState({ rows: [], counts_by_action: {}, counts_by_category: {}, total: 0, report: {} });
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');
  const [catFilter, setCatFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editingRow, setEditingRow] = useState(null);
  const [editAction, setEditAction] = useState('reviewed_ok');
  const [editNote, setEditNote] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('reviewed_ok');
  const [bulkNote, setBulkNote] = useState('');
  const [savingBulk, setSavingBulk] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (actionFilter && actionFilter !== 'all') params.append('action', actionFilter);
      if (catFilter && catFilter !== 'all') params.append('category', catFilter);
      if (search) params.append('search', search);
      const res = await api.get(`/hpcl-matcher/reports/${reportId}/review?${params.toString()}`);
      setData(res.data);
      setSelected(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [reportId, actionFilter, catFilter, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const startEdit = (row) => {
    setEditingRow(row.id);
    setEditAction(row.review_action === 'unreviewed' ? 'reviewed_ok' : row.review_action);
    setEditNote(row.review_note || '');
  };

  const saveEdit = async (rowId) => {
    try {
      await api.post(`/hpcl-matcher/reports/${reportId}/review/${rowId}`, {
        action: editAction,
        note: editNote,
      });
      toast.success('Saved');
      setEditingRow(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const toggleRow = (id) => {
    setSelected((s) => {
      const ns = new Set(s);
      if (ns.has(id)) ns.delete(id); else ns.add(id);
      return ns;
    });
  };

  const toggleAll = () => {
    if (selected.size === data.rows.length) setSelected(new Set());
    else setSelected(new Set(data.rows.map((r) => r.id)));
  };

  const bulkApply = async () => {
    if (selected.size === 0) { toast.error('Pehle rows select karo'); return; }
    setSavingBulk(true);
    try {
      const res = await api.post(`/hpcl-matcher/reports/${reportId}/review/bulk`, {
        row_ids: Array.from(selected),
        action: bulkAction,
        note: bulkNote,
      });
      toast.success(`${res.data.updated} rows updated`);
      setBulkNote('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk update failed');
    } finally {
      setSavingBulk(false);
    }
  };

  const stats = data.report?.stats || {};

  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid="hpcl-review-page">
      <div className="flex items-center gap-3">
        <Link to="/hpcl-matcher" className="text-gray-500 hover:text-gray-900" data-testid="back-btn">
          <ArrowLeft size={20} />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-[#002FA7] flex items-center gap-2">
            <ClipboardText size={26} weight="duotone" /> HPCL Reconciliation Review
          </h1>
          <p className="text-xs text-gray-600 mt-0.5">
            <span className="font-medium">{data.report?.filename}</span> ·
            Total {stats.total || 0} · Success {stats.success || 0} · Pending {stats.pending || 0} · Refunded {stats.refunded || 0}
            {typeof stats.missing === 'number' && <> · Missing {stats.missing}</>}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const params = new URLSearchParams();
            if (actionFilter && actionFilter !== 'all') params.append('action', actionFilter);
            if (catFilter && catFilter !== 'all') params.append('category', catFilter);
            openAuthenticatedDownload(`/hpcl-matcher/reports/${reportId}/review/export?${params.toString()}`);
          }}
          className="gap-1 text-xs h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          data-testid="export-reviewed-btn"
          title="Export reviewed Excel — share with CA / Auditor"
        >
          <FileXls size={14} weight="fill" /> Export reviewed
        </Button>
      </div>

      {/* Action stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {ACTIONS.map((a) => (
          <button
            key={a.value}
            onClick={() => setActionFilter(actionFilter === a.value ? 'all' : a.value)}
            className={`p-2 rounded border text-left ${actionFilter === a.value ? `border-${a.color}-500 bg-${a.color}-50` : 'border-gray-200'}`}
            data-testid={`action-stat-${a.value}`}
          >
            <div className={`text-[9px] uppercase font-bold flex items-center gap-1 ${colorChip(a.color).replace('bg-', 'text-').replace('100', '700')}`}>
              {a.icon} {a.label}
            </div>
            <div className="text-xl font-bold mt-0.5">{data.counts_by_action?.[a.value] || 0}</div>
          </button>
        ))}
      </div>

      {/* Bulk action bar — only when rows selected */}
      {selected.size > 0 && (
        <div className="bg-violet-50 border border-violet-200 rounded p-3 flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold text-violet-900">{selected.size} selected:</span>
          <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value)} className="text-xs border rounded px-2 py-1" data-testid="bulk-action-select">
            {ACTIONS.filter((a) => a.value !== 'unreviewed').map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
          <Input value={bulkNote} onChange={(e) => setBulkNote(e.target.value)} placeholder="Common note (optional)" className="h-7 text-xs flex-1 min-w-[180px] max-w-md" data-testid="bulk-note-input" />
          <Button size="sm" onClick={bulkApply} disabled={savingBulk} className="h-7 text-xs bg-violet-600 hover:bg-violet-700" data-testid="bulk-apply-btn">
            {savingBulk ? 'Saving…' : 'Apply'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setSelected(new Set())} className="h-7 text-xs">Cancel</Button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="text-xs border rounded px-2 py-1" data-testid="cat-filter">
          <option value="all">All categories</option>
          {Object.keys(data.counts_by_category || {}).map((c) => <option key={c} value={c}>{c} ({data.counts_by_category[c]})</option>)}
        </select>
        <div className="ml-auto">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search mobile / txn / consumer / note..."
            matchCount={data.rows.length}
            totalCount={data.total}
            noun="rows"
            testId="hpcl-review-search"
            inputClassName="pl-8 pr-7 h-8 text-xs w-72"
          />
        </div>
      </div>

      {/* Table */}
      <div className="border rounded overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-[10px] uppercase">
              <tr>
                <th className="px-2 py-2 w-8">
                  <input type="checkbox" checked={data.rows.length > 0 && selected.size === data.rows.length} onChange={toggleAll} data-testid="select-all" />
                </th>
                <th className="px-2 py-2 text-left">Mobile</th>
                <th className="px-2 py-2 text-left">Consumer</th>
                <th className="px-2 py-2 text-left">Status</th>
                <th className="px-2 py-2 text-left">Txn ID</th>
                <th className="px-2 py-2 text-left">Refunded</th>
                <th className="px-2 py-2 text-left">Category</th>
                <th className="px-2 py-2 text-left">In Distrib?</th>
                <th className="px-2 py-2 text-left">Review</th>
                <th className="px-2 py-2 text-left">Note</th>
                <th className="px-2 py-2 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={11} className="text-center py-6 text-gray-500">Loading…</td></tr>}
              {!loading && data.rows.length === 0 && (
                <tr><td colSpan={11} className="text-center py-6 text-gray-500">Koi rows nahi — filters change kar k try karo</td></tr>
              )}
              {data.rows.map((r) => {
                const isEditing = editingRow === r.id;
                const aDef = ACTION_LABELS[r.review_action] || ACTION_LABELS.unreviewed;
                return (
                  <tr key={r.id} className={`border-t hover:bg-gray-50 align-top ${selected.has(r.id) ? 'bg-violet-50' : ''}`}>
                    <td className="px-2 py-1.5 text-center">
                      <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleRow(r.id)} data-testid={`row-check-${r.id}`} />
                    </td>
                    <td className="px-2 py-1.5 font-mono">{r.mobile}</td>
                    <td className="px-2 py-1.5 font-mono text-gray-700">{r.consumer}</td>
                    <td className="px-2 py-1.5">{r.status}</td>
                    <td className="px-2 py-1.5 font-mono text-gray-600 break-all max-w-[150px]">{r.txn_id}</td>
                    <td className="px-2 py-1.5">
                      {r.refunded === 'No' || !r.refunded ? <span className="text-gray-400">—</span> : <span className="text-red-700 font-medium">{r.refunded}</span>}
                    </td>
                    <td className="px-2 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${categoryChip(r.category)}`}>{r.category}</span>
                    </td>
                    <td className="px-2 py-1.5">
                      <span className={`text-[10px] ${r.in_distributor === 'No' ? 'text-orange-700 font-bold' : 'text-gray-600'}`}>{r.in_distributor}</span>
                    </td>
                    <td className="px-2 py-1.5">
                      {isEditing ? (
                        <select value={editAction} onChange={(e) => setEditAction(e.target.value)} className="text-xs border rounded px-1 py-0.5" data-testid={`edit-action-${r.id}`}>
                          {ACTIONS.filter((a) => a.value !== 'unreviewed').map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                        </select>
                      ) : (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold flex items-center gap-1 w-fit ${colorChip(aDef.color)}`}>
                          {aDef.icon} {aDef.label}
                        </span>
                      )}
                      {!isEditing && r.reviewed_by_name && (
                        <div className="text-[9px] text-gray-500 mt-0.5 truncate max-w-[120px]" title={r.reviewed_by_name}>
                          by {r.reviewed_by_name}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 max-w-[200px]">
                      {isEditing ? (
                        <Input value={editNote} onChange={(e) => setEditNote(e.target.value)} className="h-7 text-xs" placeholder="Note / refund ref..." data-testid={`edit-note-${r.id}`} />
                      ) : (
                        <div className="truncate text-gray-700" title={r.review_note}>{r.review_note || <span className="text-gray-300">—</span>}</div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      {isEditing ? (
                        <div className="flex gap-1 justify-center">
                          <Button size="sm" onClick={() => saveEdit(r.id)} className="h-6 text-[10px] px-2 bg-emerald-600 hover:bg-emerald-700" data-testid={`save-${r.id}`}>Save</Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingRow(null)} className="h-6 text-[10px] px-2">X</Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => startEdit(r)} className="h-6 text-[10px] px-2 gap-1" data-testid={`edit-${r.id}`}>
                          <ChatCircle size={11} /> Review
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-[10px] text-gray-500 italic">
        ℹ Note: Review action only marks the row — refund / ledger adjustment manually karna hai. System koi auto-debit/credit nahi karega.
      </div>
    </div>
  );
}
